import { useState, useEffect } from "react";
import { LogIn, Building2, ShieldCheck, Eye, EyeOff, Fingerprint, ScanFace } from "lucide-react";
import { useAuth } from "../auth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { passwordLogin, waCheckEnabled, waLoginBegin, waLoginVerify } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { authenticateBiometric, platformBiometricAvailable, webAuthnSupported } from "../lib/webauthn";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AdminLogin() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(() => localStorage.getItem("santral_last_email") || "");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabledForEmail, setBiometricEnabledForEmail] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);

  useEffect(() => {
    if (!loading && user?.is_admin) nav("/admin");
  }, [user, loading, nav]);

  // Show a message if redirected here because non-admin logged in
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("error") === "not_admin") setErr("Bu hesabın yönetici yetkisi yok. Lütfen yönetici hesabıyla giriş yapın.");
  }, [location.search]);

  useEffect(() => {
    setBiometricSupported(webAuthnSupported());
    platformBiometricAvailable().then(setBiometricAvailable);
  }, []);

  // Check biometric enrollment status when email is entered (debounced)
  useEffect(() => {
    let cancel = false;
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) { setBiometricEnabledForEmail(false); return; }
    const t = setTimeout(async () => {
      try {
        const r = await waCheckEnabled(email);
        if (!cancel) setBiometricEnabledForEmail(!!r?.enabled);
      } catch { if (!cancel) setBiometricEnabledForEmail(false); }
    }, 350);
    return () => { cancel = true; clearTimeout(t); };
  }, [email]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !password) { setErr("E-posta ve şifre gerekli"); return; }
    setSubmitting(true);
    try {
      const res = await passwordLogin(email, password);
      localStorage.setItem("santral_last_email", email);
      const u = await refresh();
      if (u?.is_admin) {
        toast.success("Giriş başarılı");
        nav("/admin");
      } else {
        setErr(res?.user?.is_admin === false ? "Bu hesabın yönetici yetkisi yok" : "Giriş yapıldı ancak kullanıcı doğrulanamadı");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Giriş başarısız");
    } finally { setSubmitting(false); }
  };

  const startGoogle = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const bioLogin = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) { setErr("Önce e-posta girin"); return; }
    setErr(""); setBiometricBusy(true);
    try {
      const options = await waLoginBegin(email);
      const payload = await authenticateBiometric(options, email);
      await waLoginVerify(payload);
      localStorage.setItem("santral_last_email", email);
      const u = await refresh();
      if (u?.is_admin) {
        toast.success("Biyometrik giriş başarılı");
        nav("/admin");
      } else {
        setErr("Doğrulama başarılı ancak yönetici oturumu açılamadı");
      }
    } catch (e) {
      // Map WebAuthn/browser errors to friendly Turkish messages
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (e?.name === "NotAllowedError") {
        msg = "Biyometrik doğrulama iptal edildi veya zaman aşımına uğradı";
      } else if (e?.name === "SecurityError") {
        msg = "Cihaz güvenlik kontrolü başarısız oldu. Yönetici ile iletişime geçin.";
        console.error("WebAuthn SecurityError:", e.message);
      } else if (e?.name === "InvalidStateError") {
        msg = "Bu cihaz zaten kayıtlı görünüyor. Şifre ile giriş yapmayı deneyin.";
      } else if (e?.name === "NotSupportedError") {
        msg = "Bu cihaz Face ID / Touch ID desteklemiyor";
      } else {
        msg = "Biyometrik giriş yapılamadı. Şifre ile giriş yapabilirsiniz.";
        console.error("WebAuthn error:", e);
      }
      setErr(msg);
    } finally { setBiometricBusy(false); }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-4 py-24 bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" data-testid="admin-login-home-link" className="inline-block group">
            <div className="w-14 h-14 rounded-2xl bg-slate-900 mx-auto flex items-center justify-center mb-5 group-hover:bg-[#C0562F] transition-colors"><Building2 className="w-6 h-6 text-white" /></div>
            <div className="label-caps mb-2 group-hover:text-[#C0562F] transition-colors">Yönetici Paneli · Ana Sayfaya Dön</div>
            <h1 className="font-display text-3xl text-slate-900 mb-2 group-hover:text-[#C0562F] transition-colors">SANTRAL EMLAK Admin</h1>
          </Link>
          <p className="text-slate-500 text-sm">Yönetici hesabınızla giriş yapın.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="label-caps block mb-2">E-posta</label>
              <Input data-testid="login-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@..." className="h-11" />
            </div>
            <div>
              <label className="label-caps block mb-2">Şifre</label>
              <div className="relative">
                <Input data-testid="login-password" type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-10" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:text-slate-900" aria-label="Şifreyi göster">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {err && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{err}</div>}
            <button type="submit" data-testid="login-submit" disabled={submitting} className="w-full h-11 rounded-full btn-primary font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              <LogIn className="w-4 h-4" /> {submitting ? "Giriş yapılıyor…" : "Giriş Yap"}
            </button>
          </form>

          {biometricSupported && biometricAvailable && biometricEnabledForEmail && (
            <button
              onClick={bioLogin}
              disabled={biometricBusy}
              data-testid="biometric-login-btn"
              className="mt-3 w-full h-11 rounded-full bg-gradient-to-r from-[#C0562F] to-[#A04726] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_25px_-10px_rgba(192,86,47,.55)]"
            >
              <ScanFace className="w-4 h-4" /> {biometricBusy ? "Doğrulanıyor…" : "Face ID / Touch ID ile Giriş"}
            </button>
          )}

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold">veya</div>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button onClick={startGoogle} data-testid="google-login-btn" className="w-full h-11 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors text-sm font-semibold flex items-center justify-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5c1.6 0 3 .5 4.1 1.5l3-3C17.2 1.8 14.8.9 12 .9 7.4.9 3.5 3.6 1.6 7.5l3.5 2.7C6 7.4 8.8 5 12 5z"/><path fill="#4285F4" d="M23.1 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.2c-.3 1.5-1.1 2.7-2.4 3.5l3.7 2.9c2.2-2 3.6-5 3.6-8.6z"/><path fill="#FBBC05" d="M5.1 14.2c-.3-.8-.4-1.6-.4-2.5s.1-1.7.4-2.5L1.6 6.5C.6 8.1 0 10 0 12s.6 3.9 1.6 5.5l3.5-3.3z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-3l-3.7-2.9c-1 .7-2.3 1.1-3.9 1.1-3.2 0-6-2.4-6.9-5.6L1.6 17.5C3.5 21.4 7.4 24 12 24z"/></svg>
            Google ile Giriş
          </button>

          {biometricSupported && biometricAvailable && (
            <div className="mt-6 flex items-start gap-3 text-xs text-slate-500 bg-slate-50 rounded-lg p-4">
              <Fingerprint className="w-4 h-4 text-slate-700 shrink-0 mt-0.5" />
              <span>Bu cihazda Face ID / Touch ID / Parmak izi ile giriş yapabilirsiniz. İlk kez giriş yaptıktan sonra yönetici panelinde "Biyometrik Giriş" bölümünden bu cihazı kaydedin.</span>
            </div>
          )}

          <div className="mt-4 flex items-start gap-3 text-xs text-slate-500 bg-slate-50 rounded-lg p-4">
            <ShieldCheck className="w-4 h-4 text-slate-700 shrink-0 mt-0.5" />
            <span>Girişleriniz güvenli şekilde saklanır. Şifrenizi hiçbir zaman kimseyle paylaşmayın.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
